import React, { useState, useRef, useEffect } from "react";
import NavHeader from "../navigation/NavHeader";
import AsideLeft from "../aside/AsideLeft";
import AsideRight from "../aside/AsideRight";
import AsideCommunities from "../aside/AsideCommunities";
import Footer from "../navigation/Footer";
import GoUpDown from "../navigation/GoUpDown";
import Joyride, { ACTIONS, EVENTS, ORIGIN, STATUS, CallBackProps } from 'react-joyride';
import LoginAlertModal from "../auth/LoginAlertModal";
import { getSessionData } from "../../utils/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { getCommunities, getCommunityBudges } from "../../core/communities";
import { POST_VIEWED_CHANGED, removePostFromCommunityBadges } from "../../utils/postsViewed";
import { CurrentCommunityProvider } from "../../utils/CurrentCommunityProvider";
import { CommunityBadgesProvider } from "../../utils/CommunityBadgesProvider";

import CommunityAvatar from "../community/CommunityAvatar";
import FlagIcon from "../icons/Flag";
import ActivitiesBoard from "../leaderboard/ActivitiesBoard";
import LeaderBoard from "../leaderboard/LeaderBoard";
import { joyrideSteps } from "../../utils/tourData";

const finishedTourConfig = localStorage.getItem("finished-tour");
const finishedTour = finishedTourConfig === "true";

const CommunityLayout = (props) => {
    const { currentPage } = props;
    const mobileChatClasses = props?.mobileChatClasses ?? "";
    const showAsideLeft = props?.showAsideLeft ?? true;
    const onCommunitiesLoaded = props?.onCommunitiesLoaded ?? null;
    const [ads, setAds] = useState([]);
    const isFullPageWidth = props?.isFullPageWidth ?? false;
    const px = props?.px ?? "px-4";
    const overflow = props?.overflow ?? "overflow-y-auto";
    const isCB = currentPage == 'cb';
    const mustShowFooter = props?.mustShowFooter ?? (isCB == false ? true : false);

    const mainRef = useRef(null);
    const { currentCommunity, setCurrentCommunity } = props;
    const [communityBudges, setCommunityBudges] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    //check query params for comid
    const [selectedCommunityId, setSelectedCommunityId] = useState(null);
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const comid = urlParams.get('comid');
        if (comid) {
            setSelectedCommunityId(comid);
        }
    }, []);

    const [isLoginAlertModalOpen, setIsLoginAlertModalOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const steps = joyrideSteps;
    const [startTour, setStartTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const tourRenderRef = useRef(1);


    const handleStartTour = () => {
        setStartTour(true);
        tourRenderRef.current = tourRenderRef.current + 1;
    }

    const handleJoyrideCallback = (data) => {
        const { action, index, status, type } = data;

        if (action === ACTIONS.CLOSE) {
            // do something
            setStepIndex(0);
            setStartTour(false);
        }

        if (STATUS.SKIPPED == status) {
            const nextIndex = index + 2;
            if (nextIndex > steps.length - 1) {
                setStepIndex(0);
                setStartTour(false);
            } else {
                setStepIndex(nextIndex);
                setStartTour(false);
                setTimeout(() => {
                    setStartTour(true);
                }, 150);
            }
        } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
            // Update state to advance the tour
            setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
        } else if ([STATUS.FINISHED].includes(status)) {
            // You need to set our running state to false, so we can restart if we click start again.
            setStartTour(false);
        }
    }


    const handleClickRequestCommunity = (e) => {
        e.stopPropagation();
        e.preventDefault();
        let loggedInUser = getSessionData();
        if (loggedInUser === null) {
            setIsLoginAlertModalOpen(true);
        } else {
            navigate(`/community-request-form`);
        }
    }



    let loggedInUser = getSessionData();

    // Refresh badge map on route changes so counts stay in sync after navigation.
    const loggedInUserId = loggedInUser?._id ?? null;
    useEffect(() => {
        const isMountedRef = { current: true };
        const fetchBudges = async () => {
            try {
                if (loggedInUserId === null) {
                    if (isMountedRef.current) setCommunityBudges({});
                    return;
                }
                const data = await getCommunityBudges();
                if (!isMountedRef.current) return;
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    setCommunityBudges(data);
                } else {
                    setCommunityBudges({});
                }
            } catch (_err) {
                // Never let badge fetch break the layout.
                if (isMountedRef.current) setCommunityBudges({});
            }
        };
        fetchBudges();
        return () => { isMountedRef.current = false; };
    }, [loggedInUserId, location.pathname]);

    useEffect(() => {
        const handlePostViewed = (event) => {
            const postId = event?.detail?.postId;
            const forumId = event?.detail?.forumId;
            if (postId == null || postId === '' || forumId == null || forumId === '') {
                return;
            }
            setCommunityBudges((prev) => removePostFromCommunityBadges(prev, forumId, postId));
        };
        window.addEventListener(POST_VIEWED_CHANGED, handlePostViewed);
        return () => window.removeEventListener(POST_VIEWED_CHANGED, handlePostViewed);
    }, []);



    const onSelectCommunity = (community) => {
        setSelectedCommunityId(community._id);

        navigate(`/?comid=${community._id}`);
    }


    const openMenuProxyRef = useRef(null);
    const onClickViewAllCommunities = () => {

        if (openMenuContextSetterRef.current) {
            openMenuContextSetterRef.current("page-body");
        } else {
            // console.log("openMenuContextSetterRef is null in CommunityLayout");
        }

        if (openMenuProxyRef.current) {
            openMenuProxyRef.current(true);
        } else {
            // console.log("openMenuProxyRef is null in CommunityLayout");
        }
    }

    const openCommunitiesModalProxySetter = (openCommunitiesModalSetter) => {
        openMenuProxyRef.current = openCommunitiesModalSetter;
    }

    const openMenuContextSetterRef = useRef(null);
    const setOpenMenuContextProxySetter = (openMenuContextSetter) => {
        openMenuContextSetterRef.current = openMenuContextSetter;
    }




    const [isFixed, setIsFixed] = useState({
        isFixed: false,
        mainWidth: 0
    });
    const [asideLeftHeight, setAsideLeftHeight] = useState(0);
    useEffect(() => {
        const handleScroll = () => {
            // const asideLeftWrapper = document.getElementById("aside-communities-wrapper");
            // const mainRef = document.getElementById("main-ref");
            // if (asideLeftWrapper && mainRef) {
            //     setTimeout(() => {
            //         const rectangle = mainRef.getBoundingClientRect();
            //         const asideLeftHeight = asideLeftWrapper.getBoundingClientRect().height;
            //         const mainWidth = rectangle.width;
            //         let topOffset = 50;
            //         if (window.innerWidth < 1024) {
            //             topOffset = 240 + 50;
            //         }
            //         const offset = asideLeftHeight - window.innerHeight + topOffset;

            //         if (window.scrollY > offset) {
            //             setIsFixed({
            //                 isFixed: true,
            //                 mainWidth: mainWidth
            //             });
            //         } else {
            //             setIsFixed({
            //                 isFixed: false,
            //                 mainWidth: mainWidth
            //             });
            //         }
            //     }, 100);
            // }
        };

        // Attach the event listener
        window.addEventListener("scroll", handleScroll, { passive: true });

        // Cleanup on unmount
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    // className={`${currentPage === 'chat' ? 'fixed overflow-y-scroll' : 'flex min-h-full overflow-y-auto flex-col'}`}
    // style={{ "-webkit-overflow-scrolling": "touch" }}
    return (

        <div
            className={`relative flex overflow-hidden flex-col ${currentPage === 'chat' ? ' h-screen ' : ''} md:min-h-full `}
        >
            <LoginAlertModal message="Please log in to continue." isOpen={isLoginAlertModalOpen} closeFun={() => setIsLoginAlertModalOpen(false)} />

            <Joyride
                steps={steps}
                stepIndex={stepIndex}
                run={startTour}
                continuous={true}
                showProgress={true}
                showSkipButton={false}
                callback={handleJoyrideCallback}
                disableScrolling={true}
                styles={{
                    options: {
                        arrowColor: '#6b21a8',
                        primaryColor: '#6b21a8'
                    },
                }}
            />
            {/*  max-w-[80%] */}
            {/* need to put back the communities */}
            <NavHeader
                currentPage={currentPage}
                mobileChatClasses={mobileChatClasses}
                communities={[]}
                handleClickRequestCommunity={handleClickRequestCommunity}
                onSelectCommunity={onSelectCommunity}
                setCurrentCommunity={setCurrentCommunity}
                selectedCommunityId={selectedCommunityId}
                loggedInUser={loggedInUser}
                openCommunitiesModalProxy={openCommunitiesModalProxySetter}
                openMenuContextProxy={setOpenMenuContextProxySetter}
            />
            {(currentPage !== 'addpost' && !isFullPageWidth) &&
                <div className={`flex flex-col md:flex-row w-screen self-center  items-start justify-center gap-x-2 px-[5px]  lg:hidden  ${currentPage === 'chat' && mobileChatClasses.indexOf('hidden') >= 0 ? ' md:px-[8px] ' : ' md:px-[5px] pt-[57px]'}  md:pt-[58px] `}>
                    <AsideRight className="" ads={ads} context="mobile" onClickViewAllCommunities={onClickViewAllCommunities} communityBudges={communityBudges} />
                </div>
            }
            {!isFullPageWidth &&
                <div className={`flex flex-col md:flex-row w-screen self-center  items-start justify-center gap-x-2 px-[5px]  pt-0 md:pt-[5px] lg:pt-[57px] `}>

                    {currentPage !== 'chat' &&
                        null
                    }

                    {currentPage !== 'chat' &&
                        <>
                            <AsideCommunities
                                handleClickRequestCommunity={handleClickRequestCommunity}
                                onSelectCommunity={onSelectCommunity}
                                selectedCommunityId={selectedCommunityId}
                                loggedInUser={loggedInUser}
                                context={''}
                                handleStartTour={handleStartTour}
                                setCurrentCommunity={setCurrentCommunity}
                                currentPage={currentPage}
                                isFixed={isFixed}
                                communityBudges={communityBudges}
                            />
                            {isFixed.isFixed &&
                                <div className="w-full md:w-56 min-h-5 h-5 ">
                                </div>
                            }
                        </>
                    }

                    {/* flex-1 flex-grow self-stretch sm:max-w-screen md:max-w-4xl*/}
                    <main id="main-ref" className={`flex flex-col flex-1 w-full sm:max-w-screen ${currentPage === 'chat' ? 'md:overflow-x-hidden md:mt-1 lg:mt-0 xl:max-w-[62rem]' : 'md:max-w-[calc(100vw-246px)] lg:max-w-[calc(100vw-475px)] xl:max-w-2xl'}`}>
                        <CommunityBadgesProvider communityBudges={communityBudges}>
                            {props.children}
                        </CommunityBadgesProvider>
                    </main>
                    <AsideRight mainWidth={isFixed.mainWidth} className="hidden lg:block" ads={ads} onClickViewAllCommunities={onClickViewAllCommunities} communityBudges={communityBudges} />
                    {/* <div id="aside-right-buffer" className="hidden lg:block md:w-56 min-h-5 h-5 ">
                    </div> */}
                </div>
            }
            {(isFullPageWidth && isCB == false) &&
                <div className={`w-full h-[calc(100vh-57px)] ${px} ${overflow}`} >
                    <CommunityBadgesProvider communityBudges={communityBudges}>
                        {props.children}
                    </CommunityBadgesProvider>
                </div>
            }
            {isCB == true &&
                <div className={`w-full h-[calc(100vh-${mustShowFooter ? '57' : '5'}px)] ${px} ${overflow} `} >
                    <CommunityBadgesProvider communityBudges={communityBudges}>
                        {props.children}
                    </CommunityBadgesProvider>
                </div>
            }
            {mustShowFooter && 
                <Footer currentPage={currentPage} mobileChatClasses={mobileChatClasses} />
            }
            <GoUpDown />
        </div>

    );
};

export default CommunityLayout;
